"use client"

import { useMemo, useState } from "react"
import { ArrowUpRight, Info, RefreshCw } from "lucide-react"
import {
  StatBorderGrid,
  ToolPageShell,
  ToolProductCarousel,
  ToolShareBar,
} from "@/components/brand-kit"
import { TOOL_METADATA } from "@/lib/tool-metadata"
import { readCount } from "@/lib/tools/numeric-input"

const FALLBACK_RELATED = [
  "energy-cost-calculator",
  "refrigerator-size-calculator",
  "refrigerator-lifespan-calculator",
  "kitchen-space-planner",
]

function buildRelated(slugs: string[], currentSlug: string) {
  const seen = new Set<string>([currentSlug])
  const out: { title: string; category: string; description: string; href: string; time: string }[] = []
  for (const s of [...slugs, ...FALLBACK_RELATED]) {
    if (out.length === 4) break
    if (seen.has(s)) continue
    const m = TOOL_METADATA[s]
    if (!m) continue
    seen.add(s)
    out.push({
      title: m.name,
      category: m.category,
      description: m.shortDescription,
      href: `/tools/${s}`,
      time: "~30 sec",
    })
  }
  return out
}

const META = {
  name: "Refrigerator Water Filter Cost Calculator",
  category: "Energy & Cost" as const,
  mediumDescription:
    "Calculate the annual and 5-year cost of refrigerator water filters by type and replacement frequency, and compare against bottled water for the same household size.",
  faqs: [
    {
      question: "How often should I really replace my fridge water filter?",
      answer:
        "Every 6 months is the standard recommendation. Heavy-use households (5+ people, lots of dispenser use) should replace at 3 months. Filters are also typically rated for 200–300 gallons regardless of time.",
    },
    {
      question: "Are generic third-party filters safe?",
      answer:
        "Many are — but only if NSF-certified for the same contaminants the OEM filter handles (NSF 42, 53, 401). Uncertified knockoffs can release contaminants or fail to remove what they claim. Some manufacturers void warranty on non-OEM filters; check before installing.",
    },
    {
      question: "How does this compare to bottled water?",
      answer:
        "We assume 64 oz/day (USDA) per person at $0.95/day in bottled-water cost (16.9 oz @ ~$0.25 each). For a family of four, that's $1,387/yr — roughly $1,300 more than even a premium filter program.",
    },
    {
      question: "What about whole-house systems?",
      answer:
        "Whole-house carbon filters cost more upfront ($300–800) but cover dishes, laundry, and shower in addition to drinking water. The fridge filter still adds polish for taste at the dispenser.",
    },
    {
      question: "Why does flow slow down?",
      answer:
        "A clogged filter is the single most common cause of slow dispenser flow. Replace it before troubleshooting the water line — that fixes the issue in roughly 80% of cases.",
    },
  ],
  relatedTools: ["energy-cost-calculator", "refrigerator-size-calculator"],
}

export function WaterFilterCostCalculatorPageClient() {
  const [filterType, setFilterType] = useState("standard")
  const [replacementFrequency, setReplacementFrequency] = useState("6")
  const [people, setPeople] = useState("4")

  // Calculation carried over from the legacy page-client; the input guard is what
  // changed. It read `parseInt(...)` behind `if (!freq || !numPeople)`, and `!x`
  // is false for every negative — so a replacement interval of `-6` months gave
  // `12 / -6 = -2` replacements a year, a negative annual cost, and a bottled-water
  // "saving" inflated by that negative. `parseInt` also silently truncated, so a
  // household of `4.5` was answered as `4`. FRIDGE-384.
  //
  // Bounds are the real-world ranges: a filter interval is 1–24 months (the
  // manufacturers' own guidance is 3, 6 or 12), and a household sharing one
  // refrigerator is 1–20 people.
  const result = useMemo(() => {
    const freq = readCount(replacementFrequency, { min: 1, max: 24 })
    const numPeople = readCount(people, { min: 1, max: 20 })
    if (freq === null || numPeople === null) return null

    let filterCost = 0
    if (filterType === "standard") filterCost = 40
    else if (filterType === "premium") filterCost = 60
    else filterCost = 25 // generic

    const replacementsPerYear = 12 / freq
    const annualCost = filterCost * replacementsPerYear
    const fiveYearCost = annualCost * 5

    // 64 oz/day per person (USDA water guidance), 16.9 oz bottle ≈ $0.25 → $0.95/person/day
    const bottledWaterAnnual = numPeople * 0.95 * 365
    const savings = bottledWaterAnnual - annualCost

    return {
      filterCost,
      replacementsPerYear,
      annualCost,
      fiveYearCost,
      bottledWaterAnnual,
      savings,
    }
  }, [filterType, replacementFrequency, people])

  return (
    <ToolPageShell
      toolName={META.name}
      heroEyebrow={META.category}
      heroTitlePre="What does filtered water"
      heroTitleItalic="actually"
      heroTitlePost="cost per year?"
      heroSubtitle={META.mediumDescription}
      heroMeta={["⏱ ~15 sec", "USDA water guidance"]}
      heroShare={<ToolShareBar title={META.name} />}
      heroRight={
        <StatBorderGrid
          stats={[
            { value: "6 mo", label: "Standard interval", note: "Replacement" },
            { value: "200–300", label: "Gallons rated", note: "Per filter" },
            { value: "64 oz", label: "USDA daily", note: "Per person" },
            { value: "$40", label: "OEM avg", note: "Per filter" },
          ]}
          className="w-full max-w-md md:max-w-none md:w-auto mx-auto md:mx-0"
        />
      }
      faqs={META.faqs}
      relatedTools={buildRelated(META.relatedTools, "refrigerator-water-filter-cost-calculator")}
      cta={{
        title: "Match the filter to a fridge that's worth it.",
        body: "Browse French door and side-by-side refrigerators with quality water dispensers and easy-access filter housings.",
        buttonLabel: "Browse fridges with dispensers",
        buttonHref: "/collections/french-door-refrigerators",
      }}
    >
      {/* ============ CALCULATOR ============ */}
      <section className="bg-gray-50 py-8 md:py-12">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8 space-y-6">
          <div className="grid lg:grid-cols-2 gap-6 lg:items-stretch">
            <div className="bg-white rounded-xl p-6 md:p-7 border border-gray-900 flex flex-col">
              <div className="flex items-center gap-3 mb-2">
                <span className="font-mono text-[12px] font-bold tracking-[0.15em] text-[#ff9951]">STEP 01</span>
                <div className="flex-1 h-[1px] bg-gray-300" />
              </div>
              <h2
                className="text-gray-900 font-black"
                style={{ fontSize: 28, letterSpacing: "-0.025em", lineHeight: 1.05 }}
              >
                Filter type.
                <br />
                Cadence.
              </h2>
              <p className="text-[14px] text-gray-600 mt-2 leading-[1.5]">
                OEM filters cost more but never void warranty. Generic saves money — verify NSF certification.
              </p>

              <div className="mt-7 space-y-5">
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Filter type
                  </label>
                  <select
                    value={filterType}
                    onChange={(e) => setFilterType(e.target.value)}
                    className="tool-input-clean w-full px-4 py-4 text-[18px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951] bg-white"
                  >
                    <option value="standard">Standard OEM ($40)</option>
                    <option value="premium">Premium / advanced ($60)</option>
                    <option value="generic">Generic / third-party ($25)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Replacement every
                  </label>
                  <div className="relative">
                    {/* min/max/step pair with the readCount bounds above — the
                        browser hint, not the guard (a typed value still reaches
                        state, which is why the compute checks too). */}
                    <input
                      type="number"
                      min={1}
                      max={24}
                      step={1}
                      value={replacementFrequency}
                      onChange={(e) => setReplacementFrequency(e.target.value)}
                      className="tool-input-clean w-full px-4 py-4 text-[22px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951]"
                      style={{ fontVariantNumeric: "tabular-nums" }}
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[13px] font-mono text-gray-500">months</span>
                  </div>
                  <div className="text-[11px] text-gray-500 mt-1.5 flex items-center gap-1.5">
                    <Info className="w-3.5 h-3.5" /> 6 months is standard. 3 months for heavy use.
                  </div>
                </div>
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    People in household
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      min={1}
                      max={20}
                      step={1}
                      value={people}
                      onChange={(e) => setPeople(e.target.value)}
                      className="tool-input-clean w-full px-4 py-4 text-[22px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951]"
                      style={{ fontVariantNumeric: "tabular-nums" }}
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[13px] font-mono text-gray-500">people</span>
                  </div>
                </div>

                <div className="pt-2 text-[11px] uppercase tracking-[0.12em] text-gray-500 font-bold flex items-center gap-1.5">
                  <RefreshCw className="w-3 h-3" /> Updates live as you type
                </div>
              </div>
            </div>

            <div className="flex flex-col">
              <div
                className="rounded-2xl p-6 md:p-8 relative overflow-hidden text-white flex-1"
                style={{ background: "linear-gradient(135deg, #0469f0 0%, #0053c4 100%)" }}
              >
                <div
                  aria-hidden
                  className="absolute top-0 right-0 w-[340px] h-[340px] rounded-full pointer-events-none"
                  style={{ background: "rgba(255,153,81,0.35)", filter: "blur(60px)", transform: "translate(25%, -35%)" }}
                />
                <div className="relative">
                  <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-white/70 mb-8">
                    Annual filter cost
                  </div>
                  <div
                    className="font-black"
                    style={{ fontSize: "clamp(36px, 8vw, 88px)", letterSpacing: "-0.045em", lineHeight: 0.9 }}
                  >
                    {`$${result?.annualCost?.toLocaleString("en-US", { maximumFractionDigits: 0 }) ?? 0}`}
                    <span className="text-[20px] md:text-[24px] font-black tracking-tighter text-white/60 ml-2">/yr</span>
                  </div>
                  <div className="mt-4 text-[15px] text-white/85 leading-[1.5]">
                    {result
                      ? `vs $${result.bottledWaterAnnual.toLocaleString("en-US", { maximumFractionDigits: 0 })}/yr in bottled water — saves $${result.savings.toLocaleString("en-US", { maximumFractionDigits: 0 })}/yr.`
                      : "Enter filter and household details."}
                  </div>
                  <div className="grid grid-cols-2 gap-2 sm:gap-3 mt-6 md:mt-8">
                    <div className="rounded-lg p-3 sm:p-4 bg-white/10 border border-white/15">
                      <div className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-bold mb-1">5-year filter cost</div>
                      <div className="text-[18px] sm:text-[22px] font-black tracking-tight">{`$${result?.fiveYearCost?.toLocaleString("en-US", { maximumFractionDigits: 0 }) ?? "—"}`}</div>
                    </div>
                    <div className="rounded-lg p-3 sm:p-4 bg-white/10 border border-white/15">
                      <div className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-bold mb-1">5-year savings</div>
                      <div className="text-[18px] sm:text-[22px] font-black tracking-tight">{`$${result ? (result.savings * 5).toLocaleString("en-US", { maximumFractionDigits: 0 }) : "—"}`}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ FIRST CAROUSEL ============ */}
      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="Step 02 · The right dispenser"
          title="French door fridges with quality dispensers"
          description="Easy-access filter housings, NSF-certified water and ice — built for low-maintenance ownership."
          collectionType="french-door"
          fallbackCollectionType="bottom-freezer"
        />
      </section>

      {/* ============ METHODOLOGY ============ */}
      <section className="bg-gray-50 py-8 md:py-12 border-y border-gray-200">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8">
          <div className="grid md:grid-cols-[1fr_1.3fr] gap-8 md:gap-12 items-start">
            <div>
              <div className="text-[11px] font-bold uppercase tracking-[0.15em] mb-3 text-[#ff9951]">
                Under the hood
              </div>
              <h2
                className="text-gray-900 font-black"
                style={{ fontSize: "clamp(24px, 3vw, 36px)", letterSpacing: "-0.03em", lineHeight: 1.05 }}
              >
                <em className="italic text-[#ff9951]">USDA</em> water. NSF-certified filters.
              </h2>
              <p className="text-[14px] text-gray-600 mt-4 max-w-[400px] leading-[1.55]">
                Daily intake reference (64 oz / 8 cups per person) comes from USDA dietary guidance. Filter retail prices reflect typical major-retailer ranges.
              </p>
            </div>
            <ol className="list-none p-0 m-0 border-t border-gray-900">
              {[
                {
                  n: "01",
                  t: "Annual filter cost",
                  d: "Filter price × (12 ÷ replacement-month interval). 6-month cadence = 2 replacements per year.",
                },
                {
                  n: "02",
                  t: "Bottled-water comparison",
                  d: "USDA's 64 oz/day per person × $0.95 (16.9 oz × $0.25 ÷ 3.8 bottles) × 365 days × household size.",
                },
                {
                  n: "03",
                  t: "Savings projection",
                  d: "Bottled total minus filter total = annual savings. Multiply by 5 for filter-lifecycle comparison.",
                },
              ].map((s) => (
                <li
                  key={s.n}
                  className="grid grid-cols-[auto_1fr_auto] gap-4 sm:gap-7 items-center py-5 sm:py-6 border-b border-gray-900"
                >
                  <div className="font-mono text-sm font-bold tracking-[0.05em] min-w-[28px] sm:min-w-[34px] text-[#ff9951]">
                    {s.n}
                  </div>
                  <div>
                    <h3
                      className="text-gray-900"
                      style={{ fontSize: 22, fontWeight: 800, letterSpacing: "-0.02em" }}
                    >
                      {s.t}
                    </h3>
                    <p className="mt-1.5 text-[14px] text-gray-600 leading-[1.55]">{s.d}</p>
                  </div>
                  <ArrowUpRight className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500 flex-shrink-0" />
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* ============ SECOND CAROUSEL ============ */}
      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="You might also like"
          title="Side-by-side fridges with high-flow dispensers"
          description="Tall-glass-friendly external dispensers, perfect for households that drink straight from the door."
          collectionType="side-by-side"
          fallbackCollectionType="french-door"
        />
      </section>
    </ToolPageShell>
  )
}
